import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { useHead } from "@/lib/useHead";
import { articleSchema } from "@/lib/schema";
import { formatDate } from "./Blog";

const NotFound = () => {
  useHead({ title: "Article not found | First Mover OS", description: "This article does not exist.", notFound: true });
  return (
    <main className="mx-auto max-w-3xl px-6 pb-32 pt-36" data-testid="blog-post-not-found">
      <p className="eyebrow">404</p>
      <h1 className="font-display mt-5 text-4xl text-white">This article doesn't exist.</h1>
      <Link to="/blog" className="link-u mt-8 inline-block text-zinc-300" data-testid="blog-back-link">Back to the blog</Link>
    </main>
  );
};

const Faqs = ({ faqs }) => (
  <section className="mt-16 border-t border-line pt-12" data-testid="blog-post-faqs">
    <h2 className="font-display text-2xl text-white md:text-3xl">FAQ</h2>
    <div className="mt-6 divide-y divide-[#292929] border-y border-line">
      {faqs.map((f, i) => (
        <details key={i} className="group py-5">
          <summary className="cursor-pointer list-none font-display text-lg text-white">{f.question || f.q}</summary>
          <p className="mt-3 leading-relaxed text-zinc-300">{f.answer || f.a}</p>
        </details>
      ))}
    </div>
  </section>
);

const Article = ({ a }) => {
  const html = useMemo(() => DOMPurify.sanitize(a.content_html || ""), [a.content_html]);
  const faqs = Array.isArray(a.faqs) ? a.faqs.filter((f) => (f?.question || f?.q) && (f?.answer || f?.a)) : [];
  useHead({
    title: a.meta_title || a.title,
    description: a.meta_description || a.subtitle,
    path: `/blog/${a.slug}`,
    image: a.image_url,
    type: "article",
    jsonLd: a.json_ld_schema || articleSchema(a),
  });
  return (
    <main data-testid="blog-post-page" className="pt-28 md:pt-36">
      <article className="mx-auto max-w-3xl px-6 pb-24 md:pb-36" lang={a.language_code || undefined}>
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white" data-testid="blog-back-link"><ArrowLeft size={14} /> All articles</Link>
        <p className="eyebrow mt-10">{[a.category, a.read_time].filter(Boolean).join(" · ")}</p>
        <h1 className="font-display mt-5 text-4xl font-medium tracking-tight text-white sm:text-5xl" data-testid="blog-post-title">{a.title}</h1>
        {a.subtitle && <p className="mt-5 text-lg leading-relaxed text-zinc-300">{a.subtitle}</p>}
        <p className="mt-6 text-sm text-zinc-400">{[a.author_name, formatDate(a.published_at)].filter(Boolean).join(" · ")}</p>
        {a.image_url && <img src={a.image_url} alt={a.title || ""} className="mt-10 aspect-[16/9] w-full border border-line object-cover" />}
        <div className="article-body mt-12" data-testid="blog-post-content" dangerouslySetInnerHTML={{ __html: html }} />
        {faqs.length > 0 && <Faqs faqs={faqs} />}
        {Array.isArray(a.tags) && a.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2">{a.tags.map((t) => <span key={String(t)} className="border border-line px-3 py-1 text-xs text-zinc-300">{String(t)}</span>)}</div>
        )}
        <div className="mt-16 border border-line bg-[#0b0b0b] p-8">
          <p className="eyebrow">First Mover OS</p>
          <p className="font-display mt-3 text-2xl text-white">Spot the shift before the crowd does.</p>
          <Link to="/products/bundle" className="btn-cta mt-6 inline-flex" data-testid="blog-post-cta">See the Bundle</Link>
        </div>
      </article>
    </main>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    setState({ loading: true });
    if (!api) { setState({ missing: true }); return; }
    api.get(`/articles/${encodeURIComponent(slug)}`)
      .then((r) => setState(r.data?.article ? { article: r.data.article } : { missing: true }))
      .catch(() => setState({ missing: true }));
  }, [slug]);
  if (state.loading) return <main className="min-h-screen pt-36" data-testid="blog-post-loading" />;
  if (state.missing) return <NotFound />;
  return <Article a={state.article} />;
}
